import React, { useContext, useEffect, useMemo } from 'react';

// DEPENDENCIES
import { useDispatch } from 'react-redux';

// GLOBAL VARIABLES
import { ENDPOINTS } from 'endpoints.js';
import { SORT_ORDER } from 'globals.js';
import { INTERFACE } from 'pathnames.js';

// GLOBAL HELPERS
import { getListData, ListContext } from 'helpers/getListData';
import { recountActions } from 'actions.js';

// GLOBAL COMPONENTS
import Main from 'parts/main/Main';
import Table, { Filters } from 'parts/table/Table';
import Title from 'parts/title/Title';

// LOCAL VARIABLES
import { ACTIONS, COLUMNS } from 'columns.js';

// LOCAL COMPONENTS
import EstimatedDelivery from './parts/EstimatedDelivery';
import ExcelButtons from '../../tools/ExcelButtons';

// VARIABLES
const SORT_OPTIONS = [
  {
    label: 'Est. Delv Date',
    value: 'estimated_delivery_date'
  },
  {
    label: 'Sold Date',
    value: 'sales_time'
  }
];

// MAIN COMPONENT
const Order = () => {
  // REDUX
  const dispatch = useDispatch();

  // CONTEXT
  const { result: ordered = [] } = useContext(ListContext) || {};

  // Need to group in current order and allow duplicate Sales Dates
  // because can sort by Est. Delv Date
  const groupedData = useMemo(
    () =>
      ordered.reduce((acc, cur) => {
        const sortKey = 'sales_date';
        const lastGroup = acc?.[acc.length - 1];
        if (lastGroup && lastGroup.title === cur[sortKey]) {
          lastGroup.data.push(cur);
        } else {
          acc.push({
            title: cur[sortKey], //
            data: [cur]
          });
        }
        return acc;
      }, []),
    [ordered]
  );

  // EFFECTS
  useEffect(() => {
    dispatch(recountActions.toggle());
  }, [dispatch]);

  return (
    <Main>
      <Title
        documentTitle="Orders"
        title="Order"
        tools={<ExcelButtons fileName="orders" includeActiveColumns={false} />}
      />
      <Filters>
        <Filters.Date.Range useAll />
        <Filters.Button.SalesType allKey="total_records_all_RW" />
        <Filters.Button.NewUsed allKey="total_records_all_NP" />
        <Filters.Sort options={SORT_OPTIONS} />
      </Filters>
      <Table.Sorted sortedResult={groupedData} mobilePreviewKeys={['estimated_delivery_date', 'customer1_full_name']} />
    </Main>
  );
};

// EXPORT
export default getListData(Order, {
  key: 'order',
  fetchArgs: {
    endpoint: ENDPOINTS.sales.list.order
  },
  defaults: {
    sort: 'sales_time',
    order: SORT_ORDER.descending,
    startRow: null
  },
  columns: [
    {
      key: 'estimated_delivery_date',
      label: 'Est. Delv.',
      tooltip: 'Estimated Delivery time of order',
      width: 70,
      component: EstimatedDelivery
    },
    {
      ...COLUMNS.salesStatus,
      headerClassName: 'justify-content-center'
    },
    COLUMNS.salesPerson,
    COLUMNS.customer(INTERFACE.order),
    COLUMNS.ageOfVehicle,
    COLUMNS.newUsed,
    COLUMNS.purchaseLease,
    {
      ...COLUMNS.retailWholesale,
      key: 'sale_type_category_code'
    },
    COLUMNS.stockNumber,
    COLUMNS.vinNumber,
    COLUMNS.makeModel,
    COLUMNS.trades,
    COLUMNS.frontendGross,
    COLUMNS.amAssignment,
    COLUMNS.amTime,
    COLUMNS.amStatus,
    COLUMNS.fmAssignment,
    COLUMNS.fmTime,
    COLUMNS.fmStatus,
    COLUMNS.sa1,
    COLUMNS.sa2,
    COLUMNS.sa3
  ],
  actions: [ACTIONS.open(INTERFACE.order, 'sales_id')]
});
